import React, { useCallback, useMemo, useState, type ReactNode } from 'react';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { usePreloadSmartCreationModal } from '@confluence/smart-create/entry-points/usePreloadSmartCreationModal.ts';

import { SmartCreatePromptSuggestionsPreloader } from './SmartCreatePromptSuggestionsPreloader.tsx';

export type UseSmartCreatePreloadOptions = {
	isDisabled?: boolean;
	includePromptSuggestions?: boolean;
};

export function useSmartCreatePreload({
	isDisabled = false,
	includePromptSuggestions = true,
}: UseSmartCreatePreloadOptions = {}): {
	preload: () => void;
	promptSuggestionsPreloader: ReactNode;
} {
	const preloadSmartCreationModal = usePreloadSmartCreationModal();
	const [requested, setRequested] = useState(false);

	const preload = useCallback(() => {
		preloadSmartCreationModal();
		if (
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning -- Preserve existing preload exposure ordering.
			includePromptSuggestions &&
			!isDisabled &&
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning, confluence-feature-gating/no-new-feature-gates -- Preserve the existing preload rollout.
			fg('confluence_personalized_prompts_header_preload') &&
			expValEquals('confluence_personalized_prompt_suggestions', 'isEnabled', true)
		) {
			setRequested(true);
		}
	}, [includePromptSuggestions, isDisabled, preloadSmartCreationModal]);

	// The caller renders this boundary. Opting out never mounts suggestion hooks.
	const promptSuggestionsPreloader = useMemo(
		() =>
			includePromptSuggestions
				? React.createElement(SmartCreatePromptSuggestionsPreloader, {
						requested: requested && !isDisabled,
					})
				: null,
		[includePromptSuggestions, requested, isDisabled],
	);

	return { preload, promptSuggestionsPreloader };
}
